import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ChefHat, Loader2, ArrowRight, Sparkles, MessageCircle, BarChart3, Mic } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { dir, toggleLanguage, lang } = useLanguage();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || 'login';
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ email: '', password: '', name: '' });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginForm.email || !loginForm.password) { toast.error(t('auth.fillAll')); return; }
    setLoading(true);
    try { await login(loginForm.email, loginForm.password); toast.success(t('common.welcomeBack')); navigate('/dashboard'); }
    catch (err) { toast.error(err.response?.data?.detail || t('auth.loginFailed')); }
    finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerForm.email || !registerForm.password || !registerForm.name) { toast.error(t('auth.fillAll')); return; }
    if (registerForm.password.length < 6) { toast.error(t('auth.passwordTooShort')); return; }
    if (!/^[\x21-\x7E]+$/.test(registerForm.password)) { toast.error(t('auth.passwordLatinOnly')); return; }
    if (!consent) { toast.error(t('auth.consentRequired')); return; }
    setLoading(true);
    try {
      await register(registerForm.email, registerForm.password, registerForm.name);
      toast.success(t('auth.accountCreated'));
      localStorage.setItem('zouqai_needs_onboarding', 'true');
      navigate('/dashboard');
    }
    catch (err) { toast.error(err.response?.data?.detail || t('auth.registerFailed')); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex" dir={dir}>
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #0F172A 100%)' }}>
        {/* Animated orbs */}
        <div className="absolute top-1/4 right-1/4 w-64 h-64 rounded-full opacity-20" style={{ background: 'radial-gradient(circle, #D97706, transparent)', filter: 'blur(80px)' }} />
        <div className="absolute bottom-1/4 left-1/3 w-48 h-48 rounded-full opacity-15" style={{ background: 'radial-gradient(circle, #6366F1, transparent)', filter: 'blur(60px)' }} />

        <div className="relative z-10 flex flex-col justify-center px-16 text-white">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-12">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/30">
              <ChefHat className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Cairo, sans-serif' }}>{t('common.appName')}</h1>
              <p className="text-xs text-white/40">{t('common.tagline')}</p>
            </div>
          </div>

          <h2 className="text-3xl font-bold mb-4 leading-tight" style={{ fontFamily: 'Cairo, sans-serif' }}>
            {t('auth.heroTitle')}<br />
            <span className="bg-gradient-to-l from-amber-400 to-orange-500 bg-clip-text text-transparent">{t('auth.heroHighlight')}</span>
          </h2>
          <p className="text-white/50 text-sm leading-relaxed mb-10 max-w-md">
            {t('auth.heroDesc')}
          </p>

          {/* Feature pills */}
          <div className="flex flex-wrap gap-3">
            {[
              { icon: MessageCircle, text: t('auth.smartBot') },
              { icon: Mic, text: t('auth.voiceChat') },
              { icon: BarChart3, text: t('auth.aiAnalytics') },
              { icon: Sparkles, text: t('auth.siteBuilder') },
            ].map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <Icon className="w-3.5 h-3.5 text-amber-400" />
                  <span className="text-xs text-white/60">{f.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-background relative">
        {/* Language Toggle - top corner */}
        <button onClick={toggleLanguage} data-testid="auth-lang-toggle"
          className="absolute top-5 end-5 px-3 py-1.5 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
          style={{ background: 'hsl(var(--muted))' }}>
          {lang === 'ar' ? 'English' : 'عربي'}
        </button>

        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="text-center mb-8 lg:hidden">
            <Link to="/" className="inline-flex items-center gap-2 mb-3" data-testid="login-logo">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                <ChefHat className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl" style={{ fontFamily: 'Cairo, sans-serif' }}>{t('common.appName')}</span>
            </Link>
            <p className="text-xs text-muted-foreground">{t('common.tagline')}</p>
          </div>

          <div className="hidden lg:block mb-8">
            <h2 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Cairo, sans-serif' }}>{t('auth.welcome')}</h2>
            <p className="text-sm text-muted-foreground mt-1">{t('auth.loginSubtitle')}</p>
          </div>

          <Tabs defaultValue={defaultTab}>
            <TabsList className="grid w-full grid-cols-2 mb-6 h-10 rounded-xl" data-testid="auth-tabs">
              <TabsTrigger value="login" className="rounded-lg text-sm" data-testid="login-tab">{t('common.login')}</TabsTrigger>
              <TabsTrigger value="register" className="rounded-lg text-sm" data-testid="register-tab">{t('common.register')}</TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="mt-0">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label className="text-xs mb-1.5 block text-muted-foreground">{t('auth.email')}</Label>
                  <Input type="email" placeholder="name@example.com" value={loginForm.email} onChange={e => setLoginForm({ ...loginForm, email: e.target.value })}
                    data-testid="login-email-input" className="h-11 rounded-xl" dir="ltr" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <Label className="text-xs text-muted-foreground">{t('auth.password')}</Label>
                    <button type="button" className="text-[11px] text-amber-600 hover:text-amber-500 transition-colors" data-testid="forgot-password-link">
                      {t('auth.forgotPassword')}
                    </button>
                  </div>
                  <Input type="password" placeholder="••••••••" value={loginForm.password} onChange={e => setLoginForm({ ...loginForm, password: e.target.value })}
                    data-testid="login-password-input" className="h-11 rounded-xl" dir="ltr" />
                </div>
                <Button type="submit" disabled={loading} data-testid="login-submit-btn"
                  className="w-full h-11 rounded-xl font-semibold bg-gradient-to-l from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 shadow-lg shadow-amber-500/20">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('auth.loginBtn')}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register" className="mt-0">
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <Label className="text-xs mb-1.5 block text-muted-foreground">{t('auth.name')}</Label>
                  <Input type="text" placeholder="محمد عبدالله" value={registerForm.name} onChange={e => setRegisterForm({ ...registerForm, name: e.target.value })}
                    data-testid="register-name-input" className="h-11 rounded-xl" />
                </div>
                <div>
                  <Label className="text-xs mb-1.5 block text-muted-foreground">{t('auth.email')}</Label>
                  <Input type="email" placeholder="name@example.com" value={registerForm.email} onChange={e => setRegisterForm({ ...registerForm, email: e.target.value })}
                    data-testid="register-email-input" className="h-11 rounded-xl" dir="ltr" />
                </div>
                <div>
                  <Label className="text-xs mb-1.5 block text-muted-foreground">{t('auth.password')}</Label>
                  <Input type="password" placeholder="••••••••" value={registerForm.password} onChange={e => setRegisterForm({ ...registerForm, password: e.target.value })}
                    data-testid="register-password-input" className="h-11 rounded-xl" dir="ltr" />
                  <p className="text-[11px] text-muted-foreground mt-1">{t('auth.passwordHint')}</p>
                </div>
                <div className="flex items-start gap-2.5">
                  <input
                    type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-border accent-amber-500 cursor-pointer"
                    data-testid="register-consent-checkbox" id="consent"
                  />
                  <label htmlFor="consent" className="text-[11px] text-muted-foreground leading-relaxed cursor-pointer">
                    {t('auth.consentText')}{' '}
                    <Link to="/privacy" className="text-amber-500 hover:underline" target="_blank" data-testid="register-privacy-link">{t('auth.privacyPolicy')}</Link>
                    {' '}{t('auth.and')}{' '}
                    <Link to="/terms" className="text-amber-500 hover:underline" target="_blank" data-testid="register-terms-link">{t('auth.termsOfService')}</Link>
                  </label>
                </div>
                <Button type="submit" disabled={loading} data-testid="register-submit-btn"
                  className="w-full h-11 rounded-xl font-semibold bg-gradient-to-l from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 shadow-lg shadow-amber-500/20">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('auth.registerBtn')}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <div className="text-center mt-6 space-y-3">
            {/* Demo Access */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
              <div className="relative flex justify-center"><span className="bg-background px-3 text-[11px] text-muted-foreground">{t('auth.orTry')}</span></div>
            </div>
            <Link to="/onboard" data-testid="auth-demo-btn"
              className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-sm font-medium border border-border text-muted-foreground hover:text-foreground hover:bg-accent transition-colors">
              <Sparkles className="w-3.5 h-3.5 text-amber-500" />
              {t('auth.tryDemo')}
            </Link>
            <Link to="/" data-testid="auth-back-home" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 transition-colors">
              <ArrowRight className={`w-3 h-3 ${dir === 'rtl' ? 'rotate-180' : ''}`} /> {t('auth.backHome')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
